import { useState } from "react";
import { ShopifyPreview, PinterestPreviews } from "./HeroVisuals";
import { WaitlistDialog } from "./WaitlistDialog";
import { VideoDemo } from "./VideoDemo";
import { motion } from "framer-motion";
import { Link } from "@remix-run/react";
import { START_ONBOARDING_MAIN } from "@/routes.config";

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

export function Hero() {
  return (
    <>
      <div className="relative isolate overflow-hidden bg-gradient-to-b from-white via-red-50/30 to-white">
        <motion.div
          className="absolute inset-x-0 top-0 -z-10 transform-gpu overflow-hidden blur-2xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <div className="relative aspect-[1155/678] w-[72.1875rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff4694] to-[#E60023] opacity-20" />
        </motion.div>

        <div className="mx-auto flex flex-col xl:flex-row gap-10 justify-center items-center max-w-7xl px-4 pb-24 pt-10 lg:px-8 xl:py-40 lg:py-20">
          <motion.div
            className="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl lg:flex-shrink-0 lg:pt-8"
            variants={container}
            initial="hidden"
            animate="show"
          >
            <motion.div
              variants={item}
              className="inline-flex items-center gap-2 rounded-full bg-red-50 px-3 py-1 text-sm text-red-600 ring-1 ring-inset ring-red-500/20 mb-3"
            >
              <span className="font-semibold">✨ Early Access</span>
              <span className="h-1 w-1 rounded-full bg-red-500"></span>
              <span>50% Launch Discount</span>
            </motion.div>

            <motion.h1
              variants={item}
              className="mt-0 text-4xl font-bold space-x-2 text-gray-900 sm:text-5xl"
            >
              <span>Transform Your</span>
              <span className="text-red-600">Shopify Store</span>
              <span>with</span>
              <span className="text-red-600">Pinterest</span>
            </motion.h1>

            <motion.p
              variants={item}
              className="mt-6 text-lg leading-8 text-gray-600"
            >
              Automatically create, optimize, and manage your Pinterest
              presence. Get more traffic and sales while you focus on your
              business.
            </motion.p>

            <motion.div
              variants={item}
              className="mt-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center"
            >
              <Link to={START_ONBOARDING_MAIN()}>
                <button className="inline-flex items-center rounded-md bg-red-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600">
                  Get Early Access
                  <svg
                    className="ml-2 -mr-1 h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </Link>

              <span className="text-sm text-gray-500 flex items-center gap-2">
                <svg
                  className="h-5 w-5 text-green-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Try our free forever, no credit card required
              </span>
            </motion.div>

            <motion.div
              variants={item}
              className="mt-10 grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 text-sm"
            >
              <div className="relative pl-9">
                <dt className="inline font-semibold text-gray-900">
                  <svg
                    className="absolute left-1 top-1 h-5 w-5 text-red-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M5.5 13a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 13H11V9.413l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13H5.5z" />
                  </svg>
                  AI-Powered Content
                </dt>
                <dd className="inline ml-1 text-gray-600">
                  - Auto-generate engaging pins & descriptions
                </dd>
              </div>
              <div className="relative pl-9">
                <dt className="inline font-semibold text-gray-900">
                  <svg
                    className="absolute left-1 top-1 h-5 w-5 text-red-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Boost Traffic
                </dt>
                <dd className="inline ml-1 text-gray-600">
                  - Smart scheduling & performance analytics
                </dd>
              </div>
              <div className="relative pl-9">
                <dt className="inline font-semibold text-gray-900">
                  <svg
                    className="absolute left-1 top-1 h-5 w-5 text-red-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                  </svg>
                  Growing Community
                </dt>
                <dd className="inline ml-1 text-gray-600">
                  - Build engaged followers automatically
                </dd>
              </div>
              <div className="relative pl-9">
                <dt className="inline font-semibold text-gray-900">
                  <svg
                    className="absolute left-1 top-1 h-5 w-5 text-red-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.415l.707-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  One-Click Setup
                </dt>
                <dd className="inline ml-1 text-gray-600">
                  - Connect Shopify & start in minutes
                </dd>
              </div>
            </motion.div>
          </motion.div>

          <VideoDemo />
        </div>
      </div>
    </>
  );
}
